/**
 * useDraggable — pointer-capture drag-by-handle for floating panels.
 *
 * Lifted verbatim from the image-editor's AdjustmentsModal (the proven
 * in-repo drag primitive) so the global Fias AI window and future floating
 * panels share one implementation. Spread `handlers` onto the drag handle
 * (usually a header); `pos` is the accumulated translation to apply via
 * transform. Interactive children of the handle never start a drag.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

export function useDraggable() {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    pointerId: number;
    handle: HTMLElement;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Don't start drag from interactive children inside the handle.
      const target = e.target as HTMLElement;
      if (target.closest('button, input, select, textarea, a, [role="slider"]')) return;
      stateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseX: pos.x,
        baseY: pos.y,
        pointerId: e.pointerId,
        handle: e.currentTarget,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    [pos.x, pos.y],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = stateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    setPos({
      x: s.baseX + (e.clientX - s.startX),
      y: s.baseY + (e.clientY - s.startY),
    });
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = stateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    s.handle.releasePointerCapture(e.pointerId);
    stateRef.current = null;
    setDragging(false);
  }, []);

  // Bail out via `prev` when already at the origin — a fresh `{x:0, y:0}`
  // object literal would otherwise count as a state change to React,
  // commit a re-render, and (combined with `drag` being in the parent's
  // effect deps) loop forever.
  const reset = useCallback(
    () => setPos((prev) => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 })),
    [],
  );

  return useMemo(
    () => ({
      pos,
      dragging,
      reset,
      setPos,
      handlers: {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
      },
    }),
    [pos, dragging, reset, handlePointerDown, handlePointerMove, handlePointerUp],
  );
}
